import { fetchHiResource } from 'packages/apis';
import data from './SafetyQuestions/SafetyQuestions.json';

/**
 * Safe comparison function to replace eval() usage.
 * Only allows a specific set of comparison operators.
 */
const safeCompare = (leftValue: string, operator: string, rightValue: string): boolean => {
  const allowedOperators: Record<string, (a: string, b: string) => boolean> = {
    '===': (a, b) => a === b,
    '!==': (a, b) => a !== b,
    // eslint-disable-next-line eqeqeq
    '==': (a, b) => a == b,
    // eslint-disable-next-line eqeqeq
    '!=': (a, b) => a != b,
    '>': (a, b) => a > b,
    '<': (a, b) => a < b,
    '>=': (a, b) => a >= b,
    '<=': (a, b) => a <= b,
  };

  const compareFn = allowedOperators[operator];
  if (!compareFn) {
    return false;
  }

  return compareFn(leftValue, rightValue);
};

export const getRedux = async (studyID: string): Promise<Record<string, any> | undefined> => {
  const { parsedBody: redux } = await fetchHiResource(
    `/api/booking/safety-questions?studyID=${studyID}`,
    'GET',
  );
  if (redux) {
    Object.keys(redux).forEach((field) => {
      const fieldValue = redux[field];
      let replacedField = '';
      if (field.startsWith('Safety.SafetyQuestions.')) {
        replacedField = field.replace('Safety.SafetyQuestions.', '');
      } else if (field.startsWith('Safety.PatientData.')) {
        replacedField = field.replace('Safety.PatientData.', '');
      }
      if (replacedField) {
        redux[replacedField] = fieldValue;
        delete redux[field];
      }
    });
  }
  return redux;
};

export const checkIfShownWhenTrue = ({ question, checkinContext, piiContext }) => {
  if (question?.show === 'always') {
    return true;
  }
  const showWhen = question?.showWhen;
  if (showWhen?.field) {
    const { value, field, operator } = showWhen;
    if (operator === 'contains') {
      return checkinContext && checkinContext[field] && checkinContext[field].includes(value);
    }
    if (field === 'gender') {
      return piiContext && safeCompare(String(piiContext[field] ?? ''), operator, String(value));
    }
    return (
      checkinContext && safeCompare(String(checkinContext[field] ?? ''), operator, String(value))
    );
  }
  return false;
};

export const getRequiredQuestions = ({
  checkinContext,
  piiContext,
  safetyQuestions,
}: {
  checkinContext: Record<string, any>;
  piiContext: Record<string, any>;
  safetyQuestions: any[];
}): string[] => {
  // Use provided safetyQuestions if available, otherwise fallback to default data
  const questionsToCheck = safetyQuestions || data.subs;
  const nestedQuestions = [];
  const nestedSubs = (question) => {
    if (checkIfShownWhenTrue({ question, checkinContext, piiContext })) {
      if (question.type !== 'info') {
        if (question.required) {
          nestedQuestions.push(question.field);
        }
      }
    }
    if (question?.subs) {
      question.subs.map((q) => nestedSubs(q));
    }
  };

  questionsToCheck.map((question) => nestedSubs(question));
  if (nestedQuestions.includes('ReasonForVisit')) {
    nestedQuestions.splice(nestedQuestions.indexOf('ReasonForVisit'), 1);
  }

  return nestedQuestions;
};

export const checkIfRequiredQuestionsComplete = ({
  checkinContext,
  piiContext,
  safetyQuestions,
}: {
  checkinContext: Record<string, any>;
  piiContext: Record<string, any>;
  safetyQuestions?: any[];
}): number => {
  const requiredFields = getRequiredQuestions({ checkinContext, piiContext, safetyQuestions });
  return requiredFields.map((field) => !checkinContext[field]).filter((field) => !!field).length;
};

export const parseNameValue = (
  checkValue: string | { target: { name: string; value: string } },
): { name: string; value: string } => {
  let value;
  let name;
  if (typeof checkValue === 'string') {
    const splitName = checkValue.split('.');
    value = splitName.pop();
    name = splitName.join('.');
  } else {
    const { target } = checkValue;
    name = target.name;
    value = target.value;
  }
  return { name, value };
};

export const filterAutoUnselectedFields = (
  checkinContext: Record<string, any>,
  name: string,
  value: string,
): Record<string, any> => {
  const reduxObj = { ...checkinContext, [name]: value };
  const keysToDelete = [];
  const keysToSetNull = [];
  if (
    [
      'MedicalMetal.Heart.Surgery',
      'MedicalMetal.Spine.Surgery',
      'MedicalMetal.Head.Surgery',
    ].includes(name) &&
    value === 'no' &&
    checkinContext[`${name.split('.').slice(0, -1).join('.')}.Type`] &&
    Array.isArray(checkinContext[`${name.split('.').slice(0, -1).join('.')}.Type`]) &&
    checkinContext[`${name.split('.').slice(0, -1).join('.')}.Type`].length > 0
  ) {
    reduxObj[`${name.split('.').slice(0, -1).join('.')}.Type`] = [];
    Object.keys(reduxObj).forEach((key) => {
      if (
        key.includes(`${name.split('.').slice(0, -1).join('.')}.`) &&
        key !== `${name.split('.').slice(0, -1).join('.')}.Type` &&
        key !== `${name.split('.').slice(0, -1).join('.')}.Surgery`
      ) {
        keysToDelete.push(key);
      }
    });
  }
  if (name === 'NonMedicalMetal.Eyes.Injury' && value === 'no') {
    keysToDelete.push('NonMedicalMetal.Eyes.Removed', 'NonMedicalMetal.Eyes.MRISince');
  }
  if (name === 'Claustrophobic.Status' && value === 'no') {
    keysToSetNull.push('Claustrophobic.OralSedative', 'Claustrophobic.OralSedativePrescription');
  }
  if (name === 'Allergies.Status' && value === 'no') {
    keysToSetNull.push('Allergies.Types');
  }
  if (name === 'Medications.Current' && value === 'no') {
    keysToSetNull.push('Medications.Types');
  }

  if (keysToDelete.length) {
    keysToDelete.forEach((key) => delete reduxObj[key]);
  }
  if (keysToSetNull.length) {
    keysToSetNull.forEach((key) => {
      reduxObj[key] = null;
    });
  }
  return reduxObj;
};
